import { useState } from "react";
import { X, FileText, Loader2, Download } from "lucide-react";
import api from "@/lib/api";

const PDF_URL = "/dossier-rentabilidad-farmaclass.pdf";
const STORAGE_KEY = "farmaclass_dossier_lead";

const inputCls =
  "w-full px-3 py-2.5 bg-white text-slate-900 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-transparent transition-all";

/** Pide el contacto antes de entregar el dossier de rentabilidad.
 *  Si ya lo dejó antes (localStorage), abre el PDF sin volver a preguntar. */
export const DossierGate = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", pharmacy: "", email: "", phone: "", company_website: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Dinos tu nombre para poder saludarte.");
    if (!form.email.trim()) return setError("Necesitamos un email para enviarte el dossier.");
    setSending(true);
    try {
      await api.post("/leads/public", {
        ...form,
        source: "web_dossier",
        plan_interest: "no_idea",
        message: "Ha descargado el dossier de rentabilidad desde la web",
      });
      localStorage.setItem(STORAGE_KEY, "1");
      setDone(true);
      window.open(PDF_URL, "_blank", "noopener");
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="dossier-gate"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 rounded-xl bg-emerald-600 grid place-items-center shrink-0">
              <FileText className="h-5 w-5 text-white" strokeWidth={1.8} />
            </span>
            <div>
              <h3 className="font-display text-xl font-extrabold text-slate-900 leading-tight">
                {done ? "Ahí lo tienes" : "Dossier de rentabilidad"}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {done
                  ? "Se ha abierto en otra pestaña. Si tu navegador lo ha bloqueado, descárgalo aquí debajo."
                  : "Déjanos tus datos y lo abrimos al instante."}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors" data-testid="dossier-gate-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="p-6">
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3.5 rounded-xl font-bold transition-colors"
              data-testid="dossier-gate-download"
            >
              <Download className="h-4 w-4" /> Abrir el dossier
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="dossier-gate-form">
            <input
              value={form.company_website}
              onChange={set("company_website")}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">Nombre *</span>
                <input value={form.name} onChange={set("name")} className={inputCls} data-testid="dossier-name" required />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">Email *</span>
                <input value={form.email} onChange={set("email")} type="email" className={inputCls} data-testid="dossier-email" required />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">Farmacia</span>
                <input value={form.pharmacy} onChange={set("pharmacy")} className={inputCls} data-testid="dossier-pharmacy" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1.5 block">Teléfono</span>
                <input value={form.phone} onChange={set("phone")} inputMode="tel" className={inputCls} data-testid="dossier-phone" />
              </label>
            </div>

            {error && (
              <p className="text-sm text-rose-600 font-medium" data-testid="dossier-gate-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 disabled:opacity-60 text-white px-5 py-3.5 rounded-xl font-bold transition-colors"
              data-testid="dossier-gate-submit"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Ver el dossier
            </button>
            <p className="text-xs text-slate-400 leading-relaxed">
              Solo lo usamos para enviarte información de FarmaClass. Nada de spam y puedes pedirnos que
              borremos tus datos cuando quieras.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export const hasDossierLead = () => localStorage.getItem(STORAGE_KEY) === "1";
export const DOSSIER_PDF_URL = PDF_URL;
